import React, { useState, useEffect } from 'react';
import './border.css';
import { FaRandom, FaMagic, FaLightbulb, FaChevronDown, FaChevronUp, FaThumbsUp, FaTags, FaHistory } from 'react-icons/fa';
import { ImageModel, PromptPreset, PromptHistory, StyleCategory, Style } from '../types';
import { resolutionOptions, getDefaultResolution } from '../config/resolutions';

interface SidebarProps {
  models: ImageModel[];
  selectedModel: ImageModel | null;
  setSelectedModel: (model: ImageModel) => void;
  prompt: string;
  setPrompt: (prompt: string) => void;
  negativePrompt: string;
  setNegativePrompt: (prompt: string) => void;
  aspectRatio: string;
  setAspectRatio: (ratio: string) => void;
  quality: string;
  setQuality: (quality: string) => void;
  seed: string;
  setSeed: (seed: string) => void;
  count: number;
  setCount: (count: number) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  presets: PromptPreset[];
  usePreset: (preset: PromptPreset) => void;
  generateRandomPrompt: () => void;
  promptHistory: PromptHistory[]; 
  useHistoryPrompt: (historyItem: PromptHistory) => void;
  styleCategories: StyleCategory[];
  selectedStyle: Style | null;
  setSelectedStyle: (style: Style | null) => void;
  resolution: string;
  setResolution: (resolution: string) => void;
  useCustomSize: boolean;
  setUseCustomSize: (useCustomSize: boolean) => void;
  customWidth: number;
  setCustomWidth: (width: number) => void;
  customHeight: number;
  setCustomHeight: (height: number) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  models,
  selectedModel,
  setSelectedModel,
  prompt,
  setPrompt,
  negativePrompt,
  setNegativePrompt,
  aspectRatio,
  setAspectRatio,
  quality,
  setQuality,
  seed,
  setSeed,
  count,
  setCount,
  onGenerate,
  isGenerating,
  presets,
  usePreset,
  generateRandomPrompt,
  promptHistory,
  useHistoryPrompt,
  styleCategories,
  selectedStyle,
  setSelectedStyle,
  resolution,
  setResolution,
  useCustomSize,
  setUseCustomSize,
  customWidth,
  setCustomWidth,
  customHeight,
  setCustomHeight
}) => {
  // 控制各部分的展开/收合状态
  const [isNegativePromptExpanded, setIsNegativePromptExpanded] = useState<boolean>(false);
  const [isSeedExpanded, setIsSeedExpanded] = useState<boolean>(false);
  const [isPromptSuggestionsExpanded, setIsPromptSuggestionsExpanded] = useState<boolean>(false);
  const [isTagsExpanded, setIsTagsExpanded] = useState<boolean>(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false);
  const [isCombinerExpanded, setIsCombinerExpanded] = useState<boolean>(false); // 添加提示词组合器展开状态
  
  // 添加三个主面板的展开/收合状态
  const [isModelPanelExpanded, setIsModelPanelExpanded] = useState<boolean>(false); // 默认收合
  const [isPromptPanelExpanded, setIsPromptPanelExpanded] = useState<boolean>(false); // 默认收合
  const [isImageSettingsPanelExpanded, setIsImageSettingsPanelExpanded] = useState<boolean>(false); // 默认收合
  
  // 提示词组合器状态
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [combinedPrompt, setCombinedPrompt] = useState<string>('');

  // 根据选择的模型类别过滤提示词
  const [filteredPresets, setFilteredPresets] = useState<PromptPreset[]>([]);
  
  // 常用提示词标签
  const commonTags: Record<string, Array<{text: string, className: string}>> = {
    '画质增强': [
      { text: '高清细节', className: 'tag-primary' },
      { text: '4K超高清', className: 'tag-primary' },
      { text: '精细纹理', className: 'tag-primary' },
      { text: '锐利图像', className: 'tag-primary' }
    ],
    '光照效果': [
      { text: '柔和光线', className: 'tag-secondary' },
      { text: '逆光', className: 'tag-secondary' },
      { text: '黄金时段', className: 'tag-secondary' },
      { text: '体积光', className: 'tag-secondary' }
    ],
    '构图技巧': [
      { text: '黄金比例', className: 'tag-success' },
      { text: '对称构图', className: 'tag-success' },
      { text: '景深效果', className: 'tag-success' },
      { text: '广角视角', className: 'tag-success' }
    ],
    '艺术风格': [
      { text: '电影感', className: 'tag-warning' },
      { text: '梦幻氛围', className: 'tag-warning' },
      { text: '未来主义', className: 'tag-warning' },
      { text: '极简主义', className: 'tag-warning' }
    ]
  };
  
  // 添加标签到提示词
  const addTagToPrompt = (tag: string) => {
    // 如果提示词为空或者最后一个字符不是逗号或空格，则添加逗号和空格
    const separator = prompt && !prompt.endsWith(', ') && !prompt.endsWith(' ') ? ', ' : '';
    setPrompt(prompt + separator + tag);
  };
  
  // 添加或移除标签到组合器
  const toggleTagInCombiner = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(prev => prev.filter(t => t !== tag));
    } else {
      setSelectedTags(prev => [...prev, tag]);
    }
  };
  
  // 生成组合提示词
  const generateCombinedPrompt = () => {
    if (selectedTags.length === 0) return;
    
    const combined = selectedTags.join(', ');
    setCombinedPrompt(combined);
  };
  
  // 应用组合提示词
  const applyCombinedPrompt = () => {
    if (!combinedPrompt) return;
    
    if (prompt) {
      // 如果已有提示词，添加到末尾
      const separator = prompt.endsWith(', ') || prompt.endsWith(' ') ? '' : ', ';
      setPrompt(prompt + separator + combinedPrompt);
    } else {
      // 如果没有提示词，直接设置
      setPrompt(combinedPrompt);
    }
    
    // 清空组合器
    setSelectedTags([]);
    setCombinedPrompt('');
  };
  
  // 当选择的模型改变时，更新过滤后的提示词
  useEffect(() => {
    if (selectedModel && selectedModel.category && presets.length > 0) {
      const categoryPresets = presets.filter(preset => 
        preset.category === selectedModel.category
      );
      
      // 如果找到匹配的提示词，则使用它们，否则使用所有提示词
      setFilteredPresets(categoryPresets.length > 0 ? categoryPresets : presets);
      
      // 不再自动展开提示词建议，让用户手动点击展开
      // setIsPromptSuggestionsExpanded(true);
    } else {
      setFilteredPresets(presets);
      // 如果没有选择模型，保持提示词建议收合
      // setIsPromptSuggestionsExpanded(false);
    }
  }, [selectedModel, presets]);
  
  // 使用提示词预设
  const handleUsePreset = (preset: PromptPreset) => {
    usePreset(preset);
    // 使用后关闭提示词建议
    setIsPromptSuggestionsExpanded(false);
  };
  
  // 格式化日期显示
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 截断长文本
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // 当宽高比变化时，更新分辨率选项
  useEffect(() => {
    // 获取当前宽高比的默认分辨率
    const defaultResolution = getDefaultResolution(aspectRatio);
    setResolution(defaultResolution.id);
  }, [aspectRatio, setResolution]);

  return (
    <aside className="w-full md:w-80 lg:w-96 p-4 overflow-y-auto custom-scrollbar sidebar">
      <div className="flex-1 space-y-6">
        {/* 第一个主面板：模型与风格样式 */}
        <div className="gradient-border">
          {/* 内容容器 */}
          <div className="gradient-border-content">
            {/* 标题栏 */}
            <div 
              className="panel-title"
              onClick={() => setIsModelPanelExpanded(!isModelPanelExpanded)}
            >
              <h2 className="text-xl font-semibold text-always-visible text-white">模型与风格</h2>
              {isModelPanelExpanded ? 
                <FaChevronUp className="theme-text-secondary text-white" /> : 
                <FaChevronDown className="theme-text-secondary text-white" />}
            </div>
            
            {/* 面板内容 */}
            {isModelPanelExpanded && (
              <div className="panel-content">
                {/* 选择AI模型 */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-always-visible text-white">选择AI模型</h3>
                  <select
                    className="w-full px-3 py-2 theme-select style-select-scrollbar text-white"
                    value={selectedModel?.id || ''}
                    onChange={(e) => {
                      const modelId = e.target.value;
                      const model = models.find(m => m.id === modelId);
                      if (model) {
                        setSelectedModel(model);
                      }
                    }}
                  >
                    <option value="">-- 选择模型 --</option>
                    {models.map((model) => {
                      // 检查模型是否有类别
                      if (model.category) {
                        // 按类别分组显示模型
                        return null; // 先返回null，稍后会按类别分组显示
                      } else {
                        // 对于没有类别的旧模型，直接显示
                        return (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        );
                      }
                    })}
                    
                    {/* 按类别分组显示模型 */}
                    {Array.from(new Set(models.filter(m => m.category).map(m => m.category))).map(category => (
                      <optgroup key={category} label={category || ''}>
                        {models.filter(m => m.category === category).map(model => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  
                  {/* 显示选中模型的详细信息 */}
                  {selectedModel && (
                    <div className="p-3 theme-dropdown-bg rounded-lg border border-gray-700 dark:border-dark-600">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="theme-text-primary font-medium">{selectedModel.name}</h3>
                        {selectedModel.category && (
                          <span className="px-2 py-1 bg-primary-600 bg-opacity-20 rounded text-primary-400 text-xs">
                            {selectedModel.category}
                          </span>
                        )}
                      </div>
                      <p className="model-description text-sm theme-text-secondary">{selectedModel.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedModel.capabilities.map((capability, index) => (
                          <span 
                            key={index} 
                            className="px-2 py-1 theme-tag-bg theme-tag-text text-xs"
                          >
                            {capability}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* 风格样式 */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-always-visible text-white">风格样式</h3>
                  <div className="space-y-2">
                    <select
                      className="w-full px-3 py-2 theme-select style-select-scrollbar text-white"
                      value={selectedStyle ? `${selectedStyle.category}:${selectedStyle.id}` : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) {
                          setSelectedStyle(null);
                          return;
                        }
                        
                        const [categoryId, styleId] = value.split(':');
                        const category = styleCategories.find(c => c.id === categoryId);
                        if (category) {
                          const style = category.styles.find(s => s.id === styleId);
                          if (style) {
                            setSelectedStyle(style);
                          }
                        }
                      }}
                    >
                      <option value="">-- 选择风格样式 --</option>
                      {styleCategories.map((category) => (
                        <optgroup key={category.id} label={category.name}>
                          {category.styles.map((style) => (
                            <option key={style.id} value={`${category.id}:${style.id}`}>
                              {style.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    
                    {selectedStyle && (
                      <div className="mt-2 p-3 theme-dropdown-bg rounded-lg border border-gray-700 dark:border-dark-600">
                        <div className="flex items-center justify-between">
                          <div className="text-sm theme-text-primary font-medium">已选择风格</div>
                          <button 
                            onClick={() => setSelectedStyle(null)}
                            className="text-xs theme-text-secondary hover:text-white"
                          >
                            清除
                          </button>
                        </div>
                        <div className="mt-2">
                          <span className="inline-block px-2 py-1 bg-primary-600 bg-opacity-20 rounded text-primary-400 text-sm mr-2">
                            {selectedStyle.name}
                          </span>
                          <span className="text-xs theme-text-secondary">
                            ({styleCategories.find(c => c.id === selectedStyle.category)?.name})
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* 第二个主面板：提示词 */}
        <div className="gradient-border">
          {/* 内容容器 */}
          <div className="gradient-border-content">
            {/* 标题栏 */}
            <div 
              className="panel-title"
              onClick={() => setIsPromptPanelExpanded(!isPromptPanelExpanded)}
            >
              <h2 className="text-xl font-semibold text-always-visible text-white">提示词</h2>
              {isPromptPanelExpanded ? 
                <FaChevronUp className="theme-text-secondary text-white" /> : 
                <FaChevronDown className="theme-text-secondary text-white" />}
            </div>
            
            {/* 面板内容 */}
            {isPromptPanelExpanded && (
              <div className="panel-content">
                {/* 提示词 */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-always-visible text-white">提示词</h3>
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-2">
                      <button
                        onClick={generateRandomPrompt}
                        className="p-2 rounded-lg theme-dropdown-bg hover:bg-opacity-70 theme-hover-bg theme-text-secondary transition-all duration-200"
                        title="随机提示词"
                      >
                        <FaRandom />
                      </button>
                      <button
                        onClick={() => setPrompt('')}
                        className="p-2 rounded-lg theme-dropdown-bg hover:bg-opacity-70 theme-hover-bg theme-text-secondary transition-all duration-200"
                        title="清空提示词"
                      >
                        清空
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="描述你想要创建的图像..."
                    className="w-full h-32 px-3 py-2 input prompt-input-scrollbar theme-text-primary"
                  />
                </div>
                
                {/* 负面提示词 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center cursor-pointer" 
                       onClick={() => setIsNegativePromptExpanded(!isNegativePromptExpanded)}>
                    <h3 className="text-lg font-semibold text-always-visible text-white">负面提示词</h3>
                    {isNegativePromptExpanded ? 
                      <FaChevronUp className="theme-text-secondary text-white" /> : 
                      <FaChevronDown className="theme-text-secondary text-white" />}
                  </div>
                  
                  {isNegativePromptExpanded && (
                    <textarea
                      value={negativePrompt}
                      onChange={(e) => setNegativePrompt(e.target.value)}
                      placeholder="描述你不想在图像中出现的元素..."
                      className="w-full h-24 px-3 py-2 input prompt-input-scrollbar theme-text-primary"
                    />
                  )}
                </div>
                
                {/* 提示词建议 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center cursor-pointer" 
                       onClick={() => setIsPromptSuggestionsExpanded(!isPromptSuggestionsExpanded)}>
                    <h3 className="text-lg font-semibold text-always-visible text-white">提示词建议</h3>
                    {isPromptSuggestionsExpanded ? 
                      <FaChevronUp className="theme-text-secondary text-white" /> : 
                      <FaChevronDown className="theme-text-secondary text-white" />}
                  </div>
                  
                  {isPromptSuggestionsExpanded && (
                    <div className="space-y-3">
                      {/* 模型专属提示词 */}
                      {selectedModel && selectedModel.category && (
                        <div className="mb-4">
                          <div className="text-sm font-medium theme-text-primary mb-2 text-white">
                            {selectedModel.category}专属提示词
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {filteredPresets
                              .filter(preset => preset.category === selectedModel.category)
                              .map((preset, index) => (
                                <button
                                  key={`${selectedModel.category}-${index}`}
                                  onClick={() => handleUsePreset(preset)}
                                  className="p-2 text-sm text-left rounded-lg theme-dropdown-bg hover:bg-opacity-70 theme-text-secondary transition-all duration-200 border border-gray-700 dark:border-dark-600"
                                  title={preset.prompt}
                                >
                                  {preset.name}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                      
                      {/* 移除"所有提示词"部分，只显示分类提示词 */}
                      {selectedModel && (
                        <div>
                          {/* 按类别分组显示提示词 */}
                          {Array.from(new Set(presets.filter(p => p.category).map(p => p.category))).map((category) => {
                            // 跳过当前选中模型的类别，因为已经在上面显示了
                            if (selectedModel.category === category) return null;
                            
                            const categoryPresets = presets.filter(p => p.category === category);
                            if (categoryPresets.length === 0) return null;
                            
                            return (
                              <React.Fragment key={category}>
                                <div className="text-sm font-medium theme-text-primary mb-2 mt-3 text-white">
                                  {category}提示词
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  {categoryPresets.map((preset, index) => (
                                    <button
                                      key={`${category}-${index}`}
                                      onClick={() => handleUsePreset(preset)}
                                      className="p-2 text-sm text-left rounded-lg theme-dropdown-bg hover:bg-opacity-70 theme-text-secondary transition-all duration-200 border border-gray-700 dark:border-dark-600"
                                      title={preset.prompt}
                                    >
                                      {preset.name}
                                    </button>
                                  ))}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* 提示词标签 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center cursor-pointer" 
                       onClick={() => setIsTagsExpanded(!isTagsExpanded)}>
                    <h3 className="text-lg font-semibold text-always-visible text-white">
                      <span className="flex items-center gap-2">
                        <FaTags /> 提示词标签
                      </span>
                    </h3>
                    {isTagsExpanded ? 
                      <FaChevronUp className="theme-text-secondary text-white" /> : 
                      <FaChevronDown className="theme-text-secondary text-white" />}
                  </div>
                  
                  {isTagsExpanded && (
                    <div className="space-y-3">
                      {Object.keys(commonTags).map((category, index) => (
                        <div key={index} className="mb-2">
                          <div className="text-sm font-medium theme-text-primary mb-2 text-white">{category}</div>
                          <div className="grid grid-cols-2 gap-2">
                            {commonTags[category].map((tag, tagIndex) => (
                              <button
                                key={tagIndex}
                                onClick={() => addTagToPrompt(tag.text)}
                                className={`p-2 text-sm text-left rounded-lg tag-suggestion ${tag.className} theme-text-primary hover:opacity-80 transition-all duration-200 border border-gray-700 dark:border-dark-600`}
                              >
                                {tag.text}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* 提示词组合器 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center cursor-pointer" 
                       onClick={() => setIsCombinerExpanded(!isCombinerExpanded)}>
                    <h3 className="text-lg font-semibold text-always-visible text-white">
                      <span className="flex items-center gap-2">
                        <FaMagic /> 提示词组合器
                      </span>
                    </h3>
                    {isCombinerExpanded ? 
                      <FaChevronUp className="theme-text-secondary text-white" /> : 
                      <FaChevronDown className="theme-text-secondary text-white" />}
                  </div>
                  
                  {isCombinerExpanded && (
                    <div className="space-y-3">
                      {Object.keys(commonTags).map((category, index) => (
                        <div key={index} className="mb-2">
                          <div className="text-sm font-medium theme-text-primary mb-2 text-white">{category}</div>
                          <div className="grid grid-cols-2 gap-2">
                            {commonTags[category].map((tag, tagIndex) => (
                              <button
                                key={tagIndex}
                                onClick={() => toggleTagInCombiner(tag.text)}
                                className={`p-2 text-sm text-left rounded-lg tag-suggestion ${tag.className} theme-text-primary hover:opacity-80 transition-all duration-200 border border-gray-700 dark:border-dark-600 ${selectedTags.includes(tag.text) ? 'bg-primary-600 text-white' : ''}`}
                              >
                                {tag.text}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={generateCombinedPrompt}
                        className="p-2 text-sm text-left rounded-lg theme-dropdown-bg hover:bg-dark-600 theme-text-secondary transition-all duration-200 border border-gray-700 dark:border-dark-600 w-full text-white"
                      >
                        生成组合提示词
                      </button>
                      <button
                        onClick={applyCombinedPrompt}
                        className="p-2 text-sm text-left rounded-lg theme-dropdown-bg hover:bg-dark-600 theme-text-secondary transition-all duration-200 border border-gray-700 dark:border-dark-600 w-full text-white"
                      >
                        应用组合提示词
                      </button>
                      <p className="text-sm theme-text-secondary text-white">{combinedPrompt}</p>
                    </div>
                  )}
                </div>
                
                {/* 提示词历史记录 */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center cursor-pointer" 
                       onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}>
                    <h3 className="text-lg font-semibold text-always-visible text-white">
                      <span className="flex items-center gap-2">
                        <FaHistory /> 提示词历史记录
                      </span>
                    </h3>
                    {isHistoryExpanded ? 
                      <FaChevronUp className="theme-text-secondary text-white" /> : 
                      <FaChevronDown className="theme-text-secondary text-white" />}
                  </div>
                  
                  {isHistoryExpanded && (
                    <div className="space-y-3">
                      {promptHistory.map((historyItem, index) => (
                        <div key={index} className="p-3 rounded-lg border border-gray-700 dark:border-dark-600 theme-dropdown-bg">
                          <div className="flex justify-between items-center mb-2">
                            <h3 className="font-medium theme-text-primary text-white">{truncateText(historyItem.prompt, 20)}</h3>
                            <span className="text-xs theme-text-secondary text-white">{formatDate(historyItem.created_at)}</span>
                          </div>
                          <button
                            onClick={() => useHistoryPrompt(historyItem)}
                            className="p-2 text-sm text-left rounded-lg theme-dropdown-bg hover:bg-dark-600 theme-text-secondary transition-all duration-200 border border-gray-700 dark:border-dark-600 w-full text-white"
                          >
                            使用此提示词
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* 第三个主面板：图像设置 */}
        <div className="gradient-border">
          {/* 内容容器 */}
          <div className="gradient-border-content">
            {/* 标题栏 */}
            <div 
              className="panel-title"
              onClick={() => setIsImageSettingsPanelExpanded(!isImageSettingsPanelExpanded)}
            >
              <h2 className="text-xl font-semibold text-always-visible text-white">图像设置</h2>
              {isImageSettingsPanelExpanded ? 
                <FaChevronUp className="theme-text-secondary text-white" /> : 
                <FaChevronDown className="theme-text-secondary text-white" />}
            </div>
            
            {/* 面板内容 */}
            {isImageSettingsPanelExpanded && (
              <div className="panel-content">
                {/* 尺寸设置选择 */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium theme-text-secondary text-white">
                      尺寸设置方式
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="sizeMode"
                          checked={!useCustomSize}
                          onChange={() => setUseCustomSize(false)}
                          className="mr-2"
                        />
                        <span className="text-sm theme-text-secondary text-white">宽高比</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="sizeMode"
                          checked={useCustomSize}
                          onChange={() => setUseCustomSize(true)}
                          className="mr-2"
                        />
                        <span className="text-sm theme-text-secondary text-white">自定义尺寸</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* 宽高比 - 当不使用自定义尺寸时显示 */}
                {!useCustomSize && (
                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-1 text-white">
                      宽高比
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {['1:1', '16:9', '4:3', '3:4', '9:16'].map((ratio) => (
                        <button
                          key={ratio}
                          onClick={() => setAspectRatio(ratio)}
                          className={`py-2 text-center rounded ${
                            aspectRatio === ratio
                              ? 'btn-primary'
                              : 'bg-tertiary text-secondary hover:bg-opacity-80'
                          }`}
                        >
                          {ratio}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* 分辨率选择 - 当不使用自定义尺寸时显示 */}
                {!useCustomSize && (
                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-1 text-white">
                      分辨率
                    </label>
                    <select
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      className="w-full px-3 py-2 theme-select theme-dropdown-bg text-white"
                    >
                      {resolutionOptions[aspectRatio]?.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs theme-text-secondary mt-1 text-white">选择更高分辨率可能会增加生成时间</p>
                  </div>
                )}
                
                {/* 自定义尺寸 - 当使用自定义尺寸时显示 */}
                {useCustomSize && (
                  <div>
                    <label className="block text-sm font-medium theme-text-secondary mb-1 text-white">
                      自定义尺寸
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs theme-text-secondary mb-1 text-white">宽度 (0-1280)</label>
                        <input
                          type="number"
                          min="0"
                          max="1280"
                          value={customWidth}
                          onChange={(e) => setCustomWidth(Math.min(Math.max(0, parseInt(e.target.value) || 0), 1280))}
                          className="w-full px-3 py-2 input text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs theme-text-secondary mb-1 text-white">高度 (0-1280)</label>
                        <input
                          type="number"
                          min="0"
                          max="1280"
                          value={customHeight}
                          onChange={(e) => setCustomHeight(Math.min(Math.max(0, parseInt(e.target.value) || 0), 1280))}
                          className="w-full px-3 py-2 input text-white"
                        />
                      </div>
                    </div>
                    <p className="text-xs theme-text-secondary mt-1 text-white">自定义尺寸可能会影响生成质量和时间</p>
                  </div>
                )}
                
                {/* 种子值 */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center cursor-pointer" 
                       onClick={() => setIsSeedExpanded(!isSeedExpanded)}>
                    <label className="block text-sm font-medium theme-text-secondary text-white">
                      种子值 {seed && <span className="text-xs text-primary-400 text-white">({seed})</span>}
                    </label>
                    {isSeedExpanded ? 
                      <FaChevronUp className="theme-text-secondary text-white" /> : 
                      <FaChevronDown className="theme-text-secondary text-white" />}
                  </div>
                  
                  {isSeedExpanded && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={seed}
                          onChange={(e) => {
                            // 只允许输入数字
                            const value = e.target.value;
                            if (value === '' || /^[0-9]+$/.test(value)) {
                              setSeed(value);
                            }
                          }}
                          placeholder="输入种子值（可选）"
                          className="w-full px-3 py-2 input text-white"
                        />
                        <button
                          onClick={() => setSeed('')}
                          className="px-2 py-1 theme-dropdown-bg theme-text-secondary hover:text-white rounded"
                          title="清除种子值"
                        >
                          ✕
                        </button>
                      </div>
                      <p className="text-xs theme-text-secondary mt-1 text-white">使用相同的种子值可以生成相同的图片</p>
                    </div>
                  )}
                </div>
                
                {/* 生成数量 */}
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-1 text-white">
                    生成数量
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((num) => (
                      <button
                        key={num}
                        onClick={() => setCount(num)}
                        className={`py-2 text-center rounded-lg font-medium transition-all ${
                          count === num
                            ? 'bg-blue-600 text-white border-2 border-blue-600 shadow-lg transform scale-105 ring-2 ring-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                        }`}
                      >
                        {num}张
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* 画质 - 隐藏画质选择，自动使用超清 */}
                <div className="hidden">
                  <input type="hidden" value="ultra" />
                </div>
                
                {/* 生成按钮 */}
                <button
                  onClick={onGenerate}
                  disabled={isGenerating || !prompt.trim() || !selectedModel}
                  className={`btn w-full py-3 flex items-center justify-center space-x-2 ${
                    isGenerating || !prompt.trim() || !selectedModel
                      ? 'bg-tertiary text-tertiary cursor-not-allowed opacity-50'
                      : 'btn-primary'
                  }`}
                >
                  <FaMagic className={isGenerating ? 'animate-spin' : ''} />
                  <span>{isGenerating ? '生成中...' : '生成图像'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
